import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@staff-app/ui';
import { isEnterKey } from '@/lib/keyboard';

interface Props {
  initialValue?: string;
  onSubmit: (query: string) => void;
  /** Debounce delay in ms; 0 disables debounce (search fires only on Enter). */
  debounceMs?: number;
  /** Column-specific placeholder + aria-label so each box is labelled distinctly. */
  placeholder?: string;
  ariaLabel?: string;
  testId?: string;
}

/**
 * Debounced search input. Submits on Enter (IME-safe) OR after debounceMs.
 * Also fires `onSubmit('')` when the field is fully cleared so the parent
 * can reset its results list.
 */
export function CustomerSearchBox({
  initialValue = '',
  onSubmit,
  debounceMs = 300,
  placeholder,
  ariaLabel,
  testId = 'customer-search-box',
}: Props) {
  const [value, setValue] = useState(initialValue);

  // Debounce submission (fires for both populated and empty inputs).
  useEffect(() => {
    if (!debounceMs) return;
    const handle = window.setTimeout(() => onSubmit(value.trim()), debounceMs);
    return () => window.clearTimeout(handle);
    // onSubmit is allowed to change between renders (parent may inline it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, debounceMs]);

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        aria-label={ariaLabel ?? 'Search customers'}
        placeholder={placeholder ?? 'Search by company name, email, account ID, device UUID…'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (isEnterKey(e)) {
            onSubmit(value.trim());
          }
        }}
        className="pl-9"
        data-testid={testId}
      />
    </div>
  );
}
